/**
 * Shared field primitives used by every Marp template panel
 * (CssUploadPanel / PptxExtractPanel / OoxmlTemplatePanel).
 *
 * Kept in one file so the panels share the same field chrome without
 * cross-importing each other — DRY without coupling.
 */

import type * as React from "react";
import { Trans } from "@lingui/react/macro";

import { cn } from "@ando/ui";

export interface FieldLabelProps {
  readonly htmlFor: string;
  readonly required?: boolean;
  readonly children: React.ReactNode;
}

export const FieldLabel: React.FC<FieldLabelProps> = ({
  htmlFor,
  required,
  children,
}) => (
  <label
    htmlFor={htmlFor}
    className="font-medium text-[11px] text-ink-3 uppercase tracking-wide"
  >
    {children}
    {required ? <span className="ml-1 text-destructive">*</span> : null}
  </label>
);
FieldLabel.displayName = "MarpTemplateSettings.FieldLabel";

export interface ScopeRadioProps {
  /**
   * Radio-group name — required so the 3 panels (CSS / PPTX / OOXML)
   * don't accidentally share a group and cancel each other's selection.
   */
  readonly name: string;
  readonly value: string;
  readonly checked: boolean;
  readonly onChange: () => void;
  readonly label: string;
}

export const ScopeRadio: React.FC<ScopeRadioProps> = ({
  name,
  value,
  checked,
  onChange,
  label,
}) => (
  <label
    className={cn(
      "inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] transition-colors",
      checked
        ? "border-ando-orange/40 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-card/60 text-ink-2 hover:border-ando-orange/30 hover:bg-ando-orange-soft/20",
    )}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    {label}
  </label>
);
ScopeRadio.displayName = "MarpTemplateSettings.ScopeRadio";

export interface TokenSwatchTableProps {
  readonly tokens: Record<string, string>;
}

/** Visually present extracted brand tokens — color swatches when the value
 * parses as a CSS hex/rgb literal, plain text otherwise (for fonts). */
export const TokenSwatchTable: React.FC<TokenSwatchTableProps> = ({
  tokens,
}) => {
  const entries = Object.entries(tokens);
  if (entries.length === 0) {
    return (
      <p className="text-[12px] text-ink-3">
        <Trans>
          Nessun token rilevato — il file potrebbe non avere uno schema brand
          chiaro.
        </Trans>
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      {entries.map(([name, value]) => {
        const isColor = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
        return (
          <li
            key={name}
            className="flex items-center gap-2 rounded-md border border-hairline bg-card/60 px-2 py-1.5"
          >
            {isColor ? (
              <span
                aria-hidden
                className="inline-block h-5 w-5 shrink-0 rounded border border-hairline"
                style={{ backgroundColor: value }}
              />
            ) : (
              <span
                aria-hidden
                className="inline-block h-5 w-5 shrink-0 rounded border border-hairline bg-ink-3/10 text-center font-medium text-[10px] text-ink-3 leading-5"
              >
                A
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-[11.5px] text-ink-1">
                {name}
              </p>
              <p className="truncate font-mono text-[10.5px] text-ink-3">
                {value}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
TokenSwatchTable.displayName = "MarpTemplateSettings.TokenSwatchTable";
